import { rem } from "@certn/cascadia";
import {
  lineHeights,
  sizes,
  textStyles,
  weights,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import parsePhoneNumber, {
  CountryCode,
  getCountryCallingCode,
  isSupportedCountry,
} from "libphonenumber-js";
import React, { useEffect, useRef, useState } from "react";
import { AriaTextFieldProps, useTextField } from "react-aria";
import { useTranslation } from "react-i18next";

import { PhoneCountriesSelect } from "./PhoneCountriesSelect";
import { useColors } from "../../../hooks/useColors";
import { getCurrentRegion } from "../../../utils";
import { FieldErrors } from "../_assets/input-wrapper/FieldErrors";
import { FormFeedback } from "../_assets/input-wrapper/FormFeedback";
import { FormLabel } from "../_assets/input-wrapper/FormLabel";
import { FormText } from "../_assets/input-wrapper/FormText";
import { InputWrapperProps } from "../_assets/input-wrapper/InputWrapper";

export interface PhoneNumber {
  number: string;
  national_number: string;
  country?: string;
  calling_code: string;
}

export interface PhoneNumberInputProps
  extends Omit<AriaTextFieldProps, "onChange" | "value">,
    InputWrapperProps {
  name: string;
  label: string;
  info?: React.ReactNode;
  value?: PhoneNumber;
  defaultCountry?: CountryCode;
  onChange: (value: PhoneNumber | string | null) => void;
  placeholder?: string;
  description?: string;
  errorMessage?: string | boolean | null;
  successMessage?: string;
  testID?: string;
  type?: "string" | "object";
}

export function PhoneNumberInput(props: PhoneNumberInputProps) {
  const { t } = useTranslation();
  const {
    validationState,
    placeholder,
    label,
    info,
    value,
    defaultCountry = getCurrentRegion().toUpperCase() as CountryCode,
    onChange,
    testID = "phone-number-input",
    type = "object",
    isDisabled,
    ...textFieldProps
  } = props;
  const values = textStyles["input/text-field"];
  const [valid, setValid] = useState(true);
  const error = validationState === "invalid";
  const number = typeof value === "string" ? value : value?.number;
  const returnStringPhoneNumber = type === "string";
  const colors = useColors();
  const [country, setCountry] = useState<CountryCode>(() => {
    const country = parsePhoneNumber(number ?? "")?.country;

    if (country) {
      return country;
    }

    if (isSupportedCountry(defaultCountry)) {
      return defaultCountry;
    }

    return "CA";
  });
  const ref = React.useRef(null);
  const { labelProps, inputProps, descriptionProps } = useTextField(
    {
      ...textFieldProps,
      label,
      value: value?.number,
    },
    ref
  );
  const [phoneNumber, setPhoneNumber] = useState(
    () => parsePhoneNumber(number ?? "")?.formatInternational() ?? ""
  );

  // if you delete + sign phone_number won't error and will send empty string
  const ensurePlusSign = (number: string) =>
    !number.startsWith("+") ? "+" + number : number;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedInput = e.target.value
      .replace(/[^+\d\s]/g, "") // remove all characters besides numercial, spaces, and `+` for area code
      .replace(/(?!^)[+]/g, ""); // remove all `+` but the first one
    const phone = parsePhoneNumber(sanitizedInput, country);
    setCountry(phone?.country ?? country);

    if (phone?.formatInternational() === phoneNumber) {
      setPhoneNumber(ensurePlusSign(sanitizedInput));
    } else {
      const number = phone?.isValid()
        ? phone?.formatInternational()
        : sanitizedInput;

      setPhoneNumber(ensurePlusSign(number));
    }
  };

  useEffect(() => {
    const phone = parsePhoneNumber(phoneNumber || "");
    if (returnStringPhoneNumber && !phoneNumber.match(/\+[0-9]*\s[0-9]/)) {
      // If only an area code, treat it as though there is no phone number yet
      onChange(null);
      setValid(true);
      return;
    }
    setValid(phone?.isValid() || false);
    if (phone?.isValid()) {
      if (phone.country) {
        setCountry(phone.country);
      }
      if (returnStringPhoneNumber) {
        onChange(phoneNumber);
      } else {
        onChange({
          number: phoneNumber,
          national_number: phone.nationalNumber,
          country: phone.country,
          calling_code: phone.countryCallingCode,
        });
      }
    } else {
      onChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber]);

  const prevCountry = useRef(country);
  const callingCode = `+${getCountryCallingCode(country)}`;

  useEffect(() => {
    const prevCallingCode = `+${getCountryCallingCode(prevCountry.current)}`;
    const callingCode = `+${getCountryCallingCode(country)}`;

    setPhoneNumber((number) => {
      return number.replace(prevCallingCode, callingCode);
    });

    prevCountry.current = country;
  }, [country]);

  const handleClick = () => {
    setPhoneNumber((number) => {
      return number ? number : `${callingCode} `;
    });
  };

  return (
    <div data-testid={`${testID}-container`}>
      <FormLabel {...labelProps} isRequired={props.isRequired} info={info}>
        {label}
      </FormLabel>
      <div className={styles.container}>
        <PhoneCountriesSelect
          labelID={labelProps.id}
          value={country}
          onSelectionChange={setCountry}
          testID={`${testID}-country-code-select`}
        />
        <div className={css(styles.relativeBlock)} data-testid={testID}>
          <input
            {...inputProps}
            onClick={handleClick}
            data-testid={`${testID}-input`}
            value={phoneNumber}
            onChange={handleChange}
            onBlur={handleChange}
            placeholder={
              validationState === "invalid"
                ? ""
                : `${callingCode}  ${placeholder || ""}`
            }
            className={css(styles.input, {
              boxShadow: error
                ? `inset 0 0 0 1px ${colors["border-danger-input"]}`
                : props.isDisabled
                  ? `inset 0 0 0 1px ${colors["background-input-disabled"]}`
                  : `inset 0 0 0 1px ${colors["border-strong"]}`,
              border: isDisabled
                ? `1px solid ${colors["border-input-disabled"]}`
                : "none",
              color: isDisabled ? "var(--text-disabled)" : "var(--text-base)",
              fontSize: rem(sizes[values.size]),
              lineHeight: rem(lineHeights[values.lineHeight]),
              fontWeight: weights[values.weight],
              backgroundColor: props.isDisabled
                ? "var(--background-input-disabled)"
                : "var(--background-input)",
              "&:invalid": {
                border: `1px solid ${colors["border-danger-input"]}`,
              },
              "::placeholder": {
                color: "var(--text-placeholder)",
              },
              "&:hover:not(:focus)": isDisabled
                ? { borderColor: undefined }
                : !error
                  ? {
                      borderColor: "var(--border-input-active)",
                    }
                  : null,
              "&:focus": error
                ? css([
                    {
                      outline: "0",
                      borderColor: "var(--border-danger-input)",
                      background: "var(--background-base)",
                      boxShadow: "var(--focus-input-error)",
                    },
                  ])
                : css([
                    {
                      outline: "0",
                      borderColor: "var(--border-input-active)",
                      boxShadow: "var(--focus-input)",
                    },
                  ]),
            })}
            ref={ref}
          />
        </div>
      </div>
      <FormText {...descriptionProps}>{props.description}</FormText>
      <FieldErrors>{props.errorMessage}</FieldErrors>
      {!valid && !props.errorMessage && phoneNumber.length > 5 && (
        <FormFeedback type="invalid">
          {t("Please enter a valid phone number.")}
        </FormFeedback>
      )}
      <FormFeedback type="valid">{props.successMessage}</FormFeedback>
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    gap: "var(--space-2)",
  }),
  callingCode: css({
    position: "absolute",
    left: "var(--space-3)",
    top: "var(--space-2)",
  }),
  input: css({
    WebkitAppearance: "none",
    width: "100%",
    boxSizing: "border-box",
    paddingTop: "var(--space-2)",
    paddingRight: "var(--space-3)",
    paddingBottom: "var(--space-2)",
    paddingLeft: "var(--space-3)",
    borderRadius: "var(--border-radius-2)",
    fontFamily: "var(--font-family-default)",
  }),
  relativeBlock: css({
    position: "relative",
    flexGrow: 1,
  }),
};
